import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, KeyRound, Mail, ScrollText } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { value: "the-seance", label: "Episode One: The Séance — open now" },
  { value: "phantom-opera", label: "Episode Two: The Phantom Opera — Autumn 2026" },
  { value: "alchemists-vault", label: "Episode Three: The Alchemist's Vault — Winter 2026" },
  { value: "midnight-botanical", label: "Episode Four: Midnight Botanical — Spring 2027" },
];

const INCLUDES = [
  { icon: Mail, text: "The sealed experience box, posted to your door" },
  { icon: Flame, text: "Candle, wax and ritual kit — everything you need" },
  { icon: KeyRound, text: "The audio companion that guides the evening" },
  { icon: ScrollText, text: "A numbered certificate of attendance" },
];

export const Signup = ({ interest }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState(interest);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => setSelected(interest), [interest]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscribe`, {
        email,
        name: name || null,
        interest: selected,
      });
      if (data.status === "existing") {
        toast("Your name is already in the ledger.", {
          description: "We will write to you when your season opens.",
        });
      } else {
        setDone(true);
      }
    } catch (err) {
      toast.error("The spirits declined the message.", {
        description: "Please check your email address and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="join"
      data-testid="signup-section"
      className="relative border-y border-gold/15 bg-surface/30 py-24 sm:py-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 55% at 72% 50%, rgba(226, 177, 93, 0.10), transparent 70%)",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 sm:px-12 lg:grid-cols-2">
        <Reveal>
          <p className="font-cinzel text-[11px] tracking-[0.45em] text-gold/80">
            THE LEDGER
          </p>
          <h2 className="mt-6 font-serif text-3xl font-light leading-tight text-cream sm:text-5xl">
            Reserve your place <em className="italic text-gold">at the table.</em>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-sand">
            Each episode is limited to five hundred numbered boxes. Leave your
            name and address of correspondence — we write first to those in the
            ledger.
          </p>
          <ul className="mt-12 space-y-5">
            {INCLUDES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-4 text-sm text-sand">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/25">
                  <Icon className="h-4 w-4 text-gold" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative border border-gold/40 bg-surface/80 p-10 pt-14 shadow-[0_0_90px_rgba(226,177,93,0.15)] backdrop-blur-md sm:p-12 sm:pt-16">
            <div
              data-testid="signup-ledger-stamp"
              className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap border border-gold/40 bg-obsidian px-5 py-1.5 font-cinzel text-[10px] tracking-[0.3em] text-gold"
            >
              EPISODE ONE — 500 NUMBERED BOXES
            </div>
            {done ? (
              <motion.div
                data-testid="signup-success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center py-10 text-center"
              >
                <motion.div
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.1 }}
                  className="flex h-24 w-24 items-center justify-center rounded-full bg-crimson shadow-[0_0_60px_rgba(139,30,43,0.6),inset_0_4px_12px_rgba(0,0,0,0.5)]"
                >
                  <span className="absolute flex h-20 w-20 items-center justify-center rounded-full border border-cream/20 font-cinzel text-xl text-cream/90">
                    S
                  </span>
                </motion.div>
                <h3 className="mt-8 font-serif text-2xl text-cream">
                  The ledger has your name.
                </h3>
                <p className="mt-3 max-w-xs text-sm leading-relaxed text-sand">
                  Watch your letterbox. The first dispatches go out when the season
                  opens — and the house already knows you are coming.
                </p>
              </motion.div>
            ) : (
              <form data-testid="signup-form" onSubmit={submit} className="space-y-7">
                <div>
                  <label
                    htmlFor="signup-name"
                    className="font-cinzel text-[10px] tracking-[0.35em] text-gold/70"
                  >
                    YOUR NAME
                  </label>
                  <input
                    id="signup-name"
                    data-testid="signup-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="As it should appear in the ledger"
                    className="mt-3 w-full border-b border-gold/20 bg-transparent py-3 font-serif text-lg text-cream placeholder:text-ash focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label
                    htmlFor="signup-email"
                    className="font-cinzel text-[10px] tracking-[0.35em] text-gold/70"
                  >
                    ADDRESS OF CORRESPONDENCE *
                  </label>
                  <input
                    id="signup-email"
                    data-testid="signup-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@somewhere.com"
                    className="mt-3 w-full border-b border-gold/20 bg-transparent py-3 font-serif text-lg text-cream placeholder:text-ash focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label
                    htmlFor="signup-interest"
                    className="font-cinzel text-[10px] tracking-[0.35em] text-gold/70"
                  >
                    CHOOSE YOUR EPISODE
                  </label>
                  <select
                    id="signup-interest"
                    data-testid="signup-interest-select"
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    className="mt-3 w-full appearance-none border-b border-gold/20 bg-surface py-3 font-serif text-lg text-cream focus:border-gold focus:outline-none"
                  >
                    {INTERESTS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-surface">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  data-testid="signup-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-gold py-4 text-[12px] font-semibold uppercase tracking-[0.25em] text-obsidian transition-all duration-300 hover:bg-gold-glow hover:shadow-[0_0_40px_rgba(226,177,93,0.35)] disabled:opacity-50"
                >
                  {loading ? "Sealing…" : "Join the inner circle"}
                </button>
                <p className="text-center text-[11px] leading-relaxed text-ash">
                  No noise, no newsletters you did not ask for. Only word of new
                  seasons and the opening of the ledger.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
